import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

export function formatDay(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatDateTime(value) {
  if (!value) return null;
  const time = new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${formatDay(value)} ${time}`;
}

function formatExposure(seconds) {
  if (!seconds) return null;
  return seconds < 1 ? `1/${Math.round(1 / seconds)}s` : `${seconds}s`;
}

// Each fact gets its own labelled cell, so a value can be found at a glance instead
// of being picked out of one long run of text. A missing value drops its whole cell
// rather than showing "N/A". span is how many columns it takes in the phone grid.
function exifFacts(exif) {
  if (!exif) return [];
  return [
    { label: 'Captured', value: formatDateTime(exif.dateCaptured), span: 2 },
    { label: 'Camera', value: [exif.cameraMake, exif.cameraModel].filter(Boolean).join(' '), span: 2 },
    { label: 'Lens', value: exif.lens, span: 4 },
    { label: 'Aperture', value: exif.fStop && `f/${exif.fStop}` },
    { label: 'Exposure', value: formatExposure(exif.exposureTime) },
    { label: 'ISO', value: exif.iso },
    { label: 'Focal length', value: exif.focalLength && `${exif.focalLength}mm` }
  ].filter((fact) => fact.value);
}

// The details strip under a photo, shared by the homepage and the lightbox.
function PhotoMeta({ place, placeTo, filename, position, exif, factsPending, children }) {
  // While the next photo's facts load, the last photo's cells stay put with their
  // values hidden. The strip keeps its height, so the picture above does not resize
  // on every step, and no old value is ever shown against a new photo.
  const heldFacts = useRef([]);
  if (!factsPending) heldFacts.current = exifFacts(exif);

  return (
    <div className="photo-meta">
      <div className="photo-meta-title">
        {place && (placeTo
          ? <Link to={placeTo} className="photo-meta-place">{place}</Link>
          : <span className="photo-meta-place">{place}</span>)}
        <div className="photo-meta-sub">
          {filename && <span className="photo-meta-file">{filename}</span>}
          {position && <span className="photo-meta-file">{position}</span>}
        </div>
      </div>
      <dl className={`photo-meta-facts${factsPending ? ' is-pending' : ''}`}>
        {heldFacts.current.map(({ label, value, span = 1 }) => (
          <div key={label} className="photo-meta-fact" style={{ '--span': span }}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {children && <div className="photo-meta-actions">{children}</div>}
    </div>
  );
}

export default PhotoMeta;
